import { useEffect, useState } from "react";
import { LocateFixed, Loader2 } from "lucide-react";
import api from "../lib/api";
import { reverseGeocode } from "../lib/geo";

const SEL = "w-full border border-zinc-300 bg-white px-2.5 py-2 text-sm rounded-md focus:outline-none focus:border-blue-500 disabled:bg-zinc-100 disabled:text-zinc-400";
const LBL = "block text-[10px] font-700 uppercase tracking-wide text-zinc-500 mb-1";

/** Cascading location selector: Country → State/Province → County/District → City/Village.
 *  GPS button auto-detects and prefills all levels. onSave receives the location object. */
export const LocationCascade = ({ initial, onSave, onClose }) => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [sel, setSel] = useState({
    country: initial?.country || "", state: initial?.state || "",
    district: initial?.district || "", city: initial?.city || "",
  });
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsErr, setGpsErr] = useState("");

  useEffect(() => { api.get("/geo/countries").then((r) => setCountries(r.data.items || [])).catch(() => {}); }, []);

  useEffect(() => {
    setStates([]); setDistricts([]); setCities([]);
    if (!sel.country) return;
    api.get(`/geo/states?country=${encodeURIComponent(sel.country)}`).then((r) => setStates(r.data.items || [])).catch(() => {});
  }, [sel.country]);

  useEffect(() => {
    setDistricts([]); setCities([]);
    if (!sel.country || !sel.state) return;
    api.get(`/geo/districts?country=${encodeURIComponent(sel.country)}&state=${encodeURIComponent(sel.state)}`).then((r) => setDistricts(r.data.items || [])).catch(() => {});
    loadCities(sel.country, sel.state, "");
  }, [sel.country, sel.state]); // eslint-disable-line

  const loadCities = (country, state, district) => {
    const sp = new URLSearchParams({ country, limit: 300 });
    if (state) sp.set("state", state);
    if (district) sp.set("district", district);
    api.get(`/geo/cities?${sp}`).then((r) => setCities(r.data.items || [])).catch(() => setCities([]));
  };

  const pick = (level) => (e) => {
    const v = e.target.value;
    if (level === "country") setSel({ country: v, state: "", district: "", city: "" });
    else if (level === "state") setSel({ ...sel, state: v, district: "", city: "" });
    else if (level === "district") {
      setSel({ ...sel, district: v, city: "" });
      loadCities(sel.country, sel.state, v);
    } else setSel({ ...sel, city: v });
  };

  const useGps = () => {
    setGpsErr("");
    if (!navigator.geolocation) { setGpsErr("GPS not supported by this browser"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          setSel({ country: r.country || "", state: r.state || "", district: r.district || "", city: r.city || "" });
          // save immediately with coordinates — dropdowns stay open for corrections
          onSave({ country: r.country, country_code: r.country_code, state: r.state,
                   state_code: r.state_code, district: r.district, city: r.city,
                   lat: r.lat, lon: r.lon }, { keepOpen: true });
        } catch { setGpsErr("Could not resolve your position — pick manually below"); }
        setGpsBusy(false);
      },
      () => { setGpsBusy(false); setGpsErr("Location permission denied — pick manually below"); },
      { timeout: 10000, maximumAge: 300000 }
    );
  };

  const apply = () => {
    if (!sel.country) return;
    const cityDoc = cities.find((c) => c.name === sel.city);
    const countryDoc = countries.find((c) => c.name === sel.country);
    onSave({ country: sel.country, country_code: countryDoc?.alpha2_code,
             state: sel.state || undefined, district: sel.district || undefined,
             city: sel.city || undefined,
             lat: cityDoc?.latitude, lon: cityDoc?.longitude });
  };

  return (
    <div data-testid="location-cascade" className="space-y-3">
      <button onClick={useGps} disabled={gpsBusy} data-testid="cascade-gps-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-700 px-4 py-2.5 rounded-md transition-colors disabled:opacity-60">
        {gpsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
        {gpsBusy ? "Detecting your location…" : "Use my location (GPS)"}
      </button>
      {gpsErr && <p className="text-xs text-red-600" data-testid="cascade-gps-error">{gpsErr}</p>}
      <div className="grid grid-cols-2 gap-2.5">
        <div>
          <label className={LBL}>Country</label>
          <select data-testid="cascade-country" className={SEL} value={sel.country} onChange={pick("country")}>
            <option value="">Select country…</option>
            {countries.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className={LBL}>State / Province</label>
          <select data-testid="cascade-state" className={SEL} value={sel.state} onChange={pick("state")} disabled={!sel.country || states.length === 0}>
            <option value="">{states.length ? "Select state…" : "—"}</option>
            {states.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className={LBL}>County / District</label>
          <select data-testid="cascade-district" className={SEL} value={sel.district} onChange={pick("district")} disabled={!sel.state || districts.length === 0}>
            <option value="">{districts.length ? "All districts" : "—"}</option>
            {districts.map((d) => <option key={d.slug} value={d.name}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className={LBL}>City / Village</label>
          <select data-testid="cascade-city" className={SEL} value={sel.city} onChange={pick("city")} disabled={!sel.state || cities.length === 0}>
            <option value="">{cities.length ? "Select city…" : "—"}</option>
            {cities.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={apply} disabled={!sel.country} data-testid="cascade-apply"
          className="flex-1 bg-zinc-900 hover:bg-zinc-700 text-white text-sm font-700 px-4 py-2 rounded-md transition-colors disabled:opacity-40">
          Set location
        </button>
        <button onClick={onClose} data-testid="cascade-cancel" className="border border-zinc-300 text-sm font-600 px-4 py-2 rounded-md">Cancel</button>
      </div>
    </div>
  );
};
